
import { ShinseishaModel } from "@/interfaces/account.interface";
import {
  defineComponent,
  inject,
  PropType,
  reactive,
  ref,
  Ref,
  watchEffect,
} from "vue";
import { getAllKengen } from "@/api/masterTable.api";
import { DialogGenre, RowId } from "@/interfaces/common.interface";
import {
  checkAccount,
  createShinseishaIppan,
  createShinseishaKanri,
  getShinseishaById,
  updateShinseishaIppan,
  updateShinseishaKanri,
} from "@/api/account.api";
import { ElMessage } from "element-plus";
import { CheckMsg, InfoMsg } from "@/constants/message.constant";
type ValidateFieldCallback = (message?: string | Error) => void;
import { Kengen, Role } from "@/constants/role.constant";
import { useUserStore } from "@/store";
import useSelectDialog from "@/hooks/useSelectDialog";
import { GroupInfo } from "@/interfaces/kaisha.interface";
import KaishaGroup from "@/views/kaishaGroup/index.vue";

export default defineComponent({
  components: {
    KaishaGroup,
  },
  props: {
    title: String as PropType<string>, //ダイアログタイトル
    operation: String as PropType<DialogGenre>, //ダイアログ機能（登録/修正）
  },
  emits: ["search"],
  async setup(props, { emit }) {
    //親からの変数
    const dialogFormVisible = inject("dialogFormVisible") as Ref<boolean>;
    const rowId = inject("rowId") as Ref<RowId>;

    //修正前の申請者権限を格納用
    let prevKengenCd = "";

    const form = reactive({
      accountId: "",
      email: "",
      kengenCd: "",
      kaishaCd: "",
      kaishaNm: "",
      groupId: "",
      groupNm: "",
      kjnCd: "",
      kjnNm: "",
      bushoNm: "",
    } as ShinseishaModel);
    const formRef = ref();
    const loading = ref(false);
    //入力情報のチェックルール
    const rules = {
      email: [
        {
          type: "email",
          trigger: "blur",
          message: CheckMsg.CHK_MAIL2,
        },
        {
          required: true,
          validator: async (
            self: any,
            value: string,
            callback: ValidateFieldCallback
          ) => {
            if (!value) {
              callback(CheckMsg.CHK_MAIL);
            } else {
              try {
                await checkAccount({
                  id: rowId.value,
                  databaseField: "mail_address",
                  fieldName: self.name,
                  fieldValue: value,
                });
                callback();
              } catch (error: any) {
                callback(error);
              }
            }
          },
          trigger: "blur",
          name: "メールアドレス",
        },
      ],
      groupId: [
        {
          required: true,
          trigger: "change",
          message: CheckMsg.CHK_KAISHA_GROUP_NM,
        },
      ],
      kjnNm: [
        {
          required: true,
          message: CheckMsg.CHK_KJN_NM,
          trigger: "blur",
        },
      ],
      bushoNm: [
        {
          required: true,
          message: CheckMsg.CHK_BUSHO_NM,
          trigger: "blur",
        },
      ],
      kengenCd: [
        {
          required: true,
          message: CheckMsg.CHK_KENGEN,
          trigger: "blur",
        },
      ],
    };

    //フォームリセット
    const clearForm = () => {
      formRef.value.resetFields();
      //resetFieldsメソッドはselectに効かないので、selectのみ手動で値を消す
      form.kengenCd = "";
      form.kaishaNm = "";
      form.groupNm = "";
      dialogFormVisible.value = false;
      rowId.value = undefined;
    };

    //登録/修正ボタンイベント
    const handleCreateOrUpdate = () => {
      formRef.value?.validate(async (valid: unknown) => {
        if (valid) {
          loading.value = true;
          try {
            const data = {
              email: form.email,
              kengenCd: form.kengenCd,
              groupId: form.groupId,
              kjnCd: form.kjnCd,
              kjnNm: form.kjnNm,
              bushoNm: form.bushoNm,
            };
            if (props.operation === DialogGenre.Add) {
              form.kengenCd === Kengen.管理
                ? await createShinseishaKanri(data)
                : await createShinseishaIppan(data);
              ElMessage({
                type: "success",
                message: InfoMsg.INFO_SHINSEISHA_ADDED,
              });
            } else {
              prevKengenCd === Kengen.管理
                ? await updateShinseishaKanri(form.accountId, data)
                : await updateShinseishaIppan(form.accountId, data);
              ElMessage({
                type: "success",
                message: InfoMsg.INFO_SHINSEISHA_UPDATED,
              });
            }
            loading.value = false;
            clearForm();
            //再度検索
            emit("search");
          } catch (error) {
            loading.value = false;
          }
        } else {
          return false;
        }
      });
    };

    //修正モード時、選択行の情報取得
    const getShoninshaInfo = async (id: string) => {
      const {
        data: { result },
      } = await getShinseishaById(id);
      form.accountId = result.account.akaunto_id;
      form.email = result.account.mail_address;
      form.kengenCd = result.account.kengen.kengen_kubun_cd;
      form.kaishaCd = result.kaishaGroup?.kaisha.kaisha_cd;
      form.kaishaNm = result.kaishaGroup?.kaisha.kaisha_nm;
      form.groupId = result.kaishaGroup?.kaisha_group_id;
      form.groupNm = result.kaishaGroup?.kaisha_group_nm;
      form.kjnNm = result.kjn_nm;
      form.kjnCd = result.kjn_cd;
      form.bushoNm = result.busho_nm;

      //申請者修正前の権限
      prevKengenCd = form.kengenCd;
    };

    watchEffect(async () => {
      if (props.operation === DialogGenre.Edit && rowId.value) {
        await getShoninshaInfo(rowId.value as string);
      }
    });

    //会社選択画面用
    const { selectDialogVisible, handleSelect, handleCloseDialog } =
      useSelectDialog();

    const handleSelectInfo = (row: GroupInfo) => {
      form.groupId = row.groupId;
      form.groupNm = row.groupNm;
      form.kaishaCd = row.kaishaCd;
      form.kaishaNm = row.kaishaNm;
      handleCloseDialog();
    };

    //権限selectボックス初期化
    //承認者は「管理、一般」選択できる。申請者（管理）は「一般」のみ選択できる
    const role = useUserStore().getRole;
    let kengenOptions = await getAllKengen();
    kengenOptions = kengenOptions.filter((item) => {
      if (role === Role.ShinseishaKanri) {
        return item.kengen_kubun_cd === Kengen.一般;
      } else if (role === Role.ShinseishaIppan) {
        return false;
      } else {
        return true;
      }
    });

    return {
      dialogFormVisible,
      form,
      formRef,
      loading,
      rules,
      kengenOptions,
      DialogGenre,
      handleCreateOrUpdate,
      clearForm,
      handleSelectInfo,
      selectDialogVisible,
      handleSelect,
      handleCloseDialog,
    };
  },
});
